// Answer folding for typed answers, and the strict diacritics game setting.
//
// Ported from the mobile app (src/utils/textFormatting.ts, CLZMSTR-1163) so the two
// grade the same answer the same way. stripDiacritics is copied verbatim rather than
// reimplemented - the protected Cyrillic letters and the non-decomposing map are where
// a rewrite would go wrong.
//
// IMPORTANT: foldAnswerText is used as sentenceDiff's normalizeToken, so it must stay
// character-wise - folding a whole string has to equal folding its whitespace-separated
// tokens and joining them. Nothing here may trim, collapse whitespace or look at
// neighbouring words.

// Letters NFD can't decompose, so they need an explicit mapping.
const nonDecomposingLetters = [
  ["ø", "o"], ["Ø", "O"],   // Danish, Norwegian, Faroese
  ["đ", "d"], ["Đ", "D"],   // Vietnamese, Croatian, Serbian
  ["ð", "d"], ["Ð", "D"],   // Icelandic, Faroese
  ["þ", "th"], ["Þ", "TH"], // Icelandic
  ["ł", "l"], ["Ł", "L"],   // Polish
  ["ŀ", "l"], ["Ŀ", "L"],   // Catalan
  ["ħ", "h"], ["Ħ", "H"],   // Maltese
  ["ı", "i"], ["ȷ", "j"],   // Turkish, Azerbaijani
  ["ŋ", "n"], ["Ŋ", "N"],   // Sami, African languages
  ["ŧ", "t"], ["Ŧ", "T"],   // Sami
  ["ƀ", "b"], ["Ƀ", "B"],   // African languages
  ["æ", "ae"], ["Æ", "AE"], // Danish, Norwegian, Icelandic
  ["œ", "oe"], ["Œ", "OE"], // French
  ["ß", "ss"], ["ẞ", "SS"], // German
];

const nonDecomposingMap = Object.fromEntries(nonDecomposingLetters);

// Cyrillic letters that decompose but are distinct letters, not accents (мой/мои).
const protectedLetters = "йЙїЇѓЃќЌўЎ";
const placeholder = "\uE000";

const nonDecomposingRegex = new RegExp(`[${Object.keys(nonDecomposingMap).join("")}]`, "g");
const protectedRegex = new RegExp(`[${protectedLetters}]`, "g");
const placeholderRegex = new RegExp(placeholder, "g");

/**
 * Removes diacritics so "é" compares equal to "e". Used when the strict
 * diacritics game setting is off (CLZMSTR-1163).
 *
 * Only U+0300-U+036F is stripped, deliberately: Japanese dakuten, Arabic harakat and
 * Hebrew niqqud are outside that range and must keep counting.
 */
export const stripDiacritics = (str) => {
  const preserved = [];
  return str
    .replace(protectedRegex, (c) => {
      preserved.push(c);
      return placeholder;
    })
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .normalize("NFC")
    .replace(nonDecomposingRegex, (c) => nonDecomposingMap[c])
    .replace(placeholderRegex, () => preserved.shift() || "");
};

// Missing means on: settings saved before this existed have no strictDiacritics key, and
// mobile treats undefined as strict too.
export const isStrictDiacriticsSetting = (gameSettings) => (gameSettings || {}).strictDiacritics !== "off";

// The character-wise half of mobile's normalizeText. The rest of it - trimming and
// stripping edge punctuation, including a leading ¿ or ¡ - is not character-wise, and
// web already handles the trailing side in sentenceDiff and the cloze trim.
export function foldAnswerText(str, { strictDiacritics = true, targetLanguageIso = null } = {}) {
  let result = String(str ?? "").normalize("NFC");
  // Hebrew presentation forms (U+FB1D-U+FB4F) carry their niqqud precomposed; decompose
  // them so they match the same letters typed on an ordinary keyboard.
  result = result.replace(/[\uFB1D-\uFB4F]/g, (ch) => ch.normalize("NFKD"));
  result = result.toLowerCase();
  // treat ё as е for russian
  if(targetLanguageIso === "ru") {
    result = result.replace(/ё/g, "е");
  }
  result = result.replace(/[\u2018\u2019]/g, "'").replace(/[\u201C\u201D]/g, "\"");
  result = result.replace(/ς/g, "σ");

  return strictDiacritics ? result : stripDiacritics(result);
}

// The underscore skeleton over the full-sentence input, revealing each letter once it
// has been typed. With strict off one source character can fold to two typed ones
// (ß -> ss), so the typed side keeps its own cursor and advances by however long the
// source character folds to, instead of sharing an index with the source.
export function buildTranscribeMask(plainText, typedText, { fold, punctuationRegex }) {
  const typed = fold(typedText || "");
  let cursor = 0;

  return plainText.split("").map((x) => {
    const folded = fold(x);
    const matched = !!folded && typed.substr(cursor, folded.length) === folded;
    cursor += folded.length;

    if(matched || /\s/.test(x) || !folded) {
      return x;
    }
    return x.match(punctuationRegex) ? x : "_";
  }).join("");
}
